import React from "react";
import ReactDOM from "react-dom/client";
import {
  createTheme,
  MantineProvider,
  Switch,
  v8CssVariablesResolver,
} from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { MantineEmotionProvider } from "@mantine/emotion";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/carousel/styles.css";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  HttpLink,
  from,
  Observable,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import Cookies from "js-cookie";
import { Notifications } from "@mantine/notifications";
import posthog from "posthog-js";
import { setFeatureFlagEvaluationProperties } from "@jasper/shared";
import { PostHogProvider } from "posthog-js/react";

import App from "/@/App.tsx";
import { getApiUrl } from "/@/config";
import "./i18n";

const mantineTheme = createTheme({
  defaultRadius: "sm",
  fontWeights: {
    medium: "500",
  },
  components: {
    Switch: Switch.extend({
      defaultProps: {
        withThumbIndicator: false,
      },
    }),
  },
});

posthog.init("phc_7D2O0Q1WkuPNnLIvQjGJB3XpgLWphvuNrSHd3zOk7uU", {
  api_host: "https://eu.i.posthog.com",
  person_profiles: "identified_only",
  loaded: posthog => {
    posthog.register({
      labs_version: import.meta.env.VITE_APP_VERSION,
      environment: import.meta.env.MODE,
      host: window.location.host,
    });
    setFeatureFlagEvaluationProperties(posthog);
  },
});

const httpLink = new HttpLink({
  uri: `${getApiUrl()}/graphql`,
  credentials: "include",
});

import axios from "axios";
const refreshApi = axios.create({
  baseURL: `${getApiUrl()}/`,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

const refreshTokenLink = onError(({ graphQLErrors, forward, operation }) => {
  let hasToRefresh = false;
  if (graphQLErrors) {
    for (let i = 0; i < graphQLErrors.length; i++) {
      if ("originalError" in graphQLErrors[i].extensions) {
        const statusCode = (
          graphQLErrors[i].extensions.originalError as {
            statusCode: number;
            message: string;
          }
        ).statusCode;
        if (statusCode === 401) {
          hasToRefresh = true;
        }
      }
    }
  }
  if (hasToRefresh) {
    Cookies.remove("Authentication");
    return new Observable(observer => {
      refreshApi
        .get("auth/refresh-token")
        .then(() => {
          const subscriber = {
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          };
          forward(operation).subscribe(subscriber);
        })
        .catch(err => {
          window.location.assign("/");
          observer.error(err);
        });
    });
  }
});

const client = new ApolloClient({
  link: from([refreshTokenLink, httpLink]),
  cache: new InMemoryCache(),
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ApolloProvider client={client}>
      <MantineProvider
        theme={mantineTheme}
        cssVariablesResolver={v8CssVariablesResolver}
      >
        <MantineEmotionProvider>
          <ModalsProvider>
            <Notifications
              position="bottom-center"
              pauseResetOnHover="notification"
            />
            <PostHogProvider client={posthog}>
              <App />
            </PostHogProvider>
          </ModalsProvider>
        </MantineEmotionProvider>
      </MantineProvider>
    </ApolloProvider>
  </React.StrictMode>
);
